import { getOwner } from "@ember/application"
import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import analyticsOnly from "cc-frontend/lib/actions/analytics-only"
import type Router from "cc-frontend/router"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type Rpc from "cc-frontend/services/rpc"
import type Session from "cc-frontend/services/session"
import type SessionStore from "cc-frontend/services/session-store"
import type SocketService from "cc-frontend/services/socket"
import { EMAIL_REGEX } from "cc-frontend/utils/email"
import { find, isNil } from "lodash-es"
import { TrackedObject, tracked } from "tracked-built-ins"

interface Args {
  isSignRoute: boolean
  isSignInRoute: boolean
  isSignUpRoute: boolean
  isResetPasswordRoute: boolean
  signUpStep: number
  incorrectPassword: string
  email: string
}

export default class UserSigns extends Component<Args> {
  @service dispatcher!: Dispatcher
  @service socket!: SocketService
  @service session!: Session
  @service finder
  @service appState
  @service rpc!: Rpc
  @service router

  componentId = "user-signs"

  @service declare sessionStore: SessionStore

  // So we can track when we're doing a sign in/up
  @tracked signInProgress = false

  state = new TrackedObject({
    // email & pw
    tempEmail: null as string | null,
    emailConfirmation: null as string | null,
    tempPassword: null as string | null,

    // name & role
    salutation: "Mrs",
    firstName: null as string | null,
    lastName: null as string | null,
    educationRole: null as string | null,

    // planbook
    planbookTitle: "My Awesome Classroom",

    //errors
    currentError: null as
      | null
      | "INCORRECT_PASSWORD"
      | "NOT_MATCHING_EMAIL"
      | "INVALID_EMAIL"
      | "INVALID_PASSWORD"
      | "ALREADY_CREATED_ACCOUNT_BUT_PASSWORDS_DONT_MATCH"
      | "ENTER_EMAIL_BEFORE_RESETTING_PASSWORD"
      | "FULL_NAME"
      | "EDUCATION_ROLE"
      | "GENERIC"
      | "USER_NOT_FOUND"
      | "NO_PASSWORD_CREATED"
      | "NO_PASSWORD_BECAUSE_GOOGLE_AUTH"
      | "EMAIL_NOT_DELIVERABLE"
      | "LOGIN_RATE_LIMITED",
  })

  checkEmail(email) {
    this.rpc.execute("USER_CHECK_FOR_ACCOUNT", email).then((data) => {
      data.attributes.userExists
        ? this.router.transitionTo("user.sign-in")
        : this.router.transitionTo("user.sign-up")
    })
  }

  @computed("args.isSignInRoute", "args.isSignUpRoute", "args.isResetPasswordRoute")
  get showPasswordField() {
    return this.args.isSignInRoute || this.args.isSignUpRoute
  }

  /**
   * Validates the email entered, also notifying the user if invalid.
   */
  validateEmail() {
    const isValidEmail = this.state.tempEmail !== null && EMAIL_REGEX.test(this.state.tempEmail)

    if (isValidEmail) return true
    else {
      this.state.currentError = "INVALID_EMAIL"
      return false
    }
  }

  /**
   * Validates the password entered, also notifying the user if invalid.
   *
   * Note: This is only called when signing up (setting the password).
   * When signing in, we always send the password to the server in case it
   * gives us a more detailed error message (e.g. "try Google auth").
   */
  validatePassword() {
    const isValidPassword = (this.state.tempPassword ?? "").length >= 8

    if (isValidPassword) return true
    else {
      this.state.currentError = "INVALID_PASSWORD"
      return false
    }
  }

  @action goToResetPasswordPage() {
    this.state.currentError = null
    this.router.transitionTo("user.reset-password")
  }

  @action goToSign() {
    this.state.currentError = null
    this.router.transitionTo("user.sign")
  }

  @action goToSignUp() {
    this.state.currentError = null
    this.router.transitionTo("user.sign-up")
  }

  @action resetPassword(e) {
    e.preventDefault()
    this.state.currentError = null

    let isValidEmailResult = this.state.tempEmail !== null && EMAIL_REGEX.test(this.state.tempEmail)
    if (!isValidEmailResult) {
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({ titleForAnalytics: "User - Reset Password Error - Email is blank" })
      )
      this.state.currentError = "ENTER_EMAIL_BEFORE_RESETTING_PASSWORD"
    } else {
      this.rpc
        .execute("SEND_RESET_PASSWORD_EMAIL", this.state.tempEmail)
        .then((ajaxResult) => {
          if (ajaxResult.type === "error") {
            this._sendError(ajaxResult)
          } else {
            this.router.transitionTo("user.reset-password-confirmation")
          }
        })
        .catch((ajaxResult) => this._sendError(ajaxResult))
    }
  }

  _sendError(ajaxResult) {
    if (ajaxResult.code === "USER_NOT_FOUND") {
      this.state.currentError = "USER_NOT_FOUND"
    } else if (ajaxResult.code === "EMAIL_NOT_DELIVERABLE") {
      this.state.currentError = "EMAIL_NOT_DELIVERABLE"
    } else {
      this.state.currentError = "GENERIC"
    }
    this.dispatcher.dispatchPreparedAction(
      analyticsOnly({ titleForAnalytics: "User - Reset Password Error - Server Error" })
    )
  }

  @action signInWithGoogleCallback(response) {
    if (response.action === "SIGNED_IN") {
      this.handleSignInResponse(response)
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({
          titleForAnalytics: "User - Sign In",
          context: {
            Method: "Google",
          },
        })
      )
    } else {
      this.handleSignUpResponse(response)
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({
          titleForAnalytics: "User - Sign Up",
          context: {
            Method: "Google",
            value: parseFloat("0"),
            currency: "USD",
          },
        })
      )
    }
  }

  @action
  signIt(event) {
    event.preventDefault()
    this.state.currentError = null

    // Send to analytics
    this.dispatcher.dispatchPreparedAction(
      analyticsOnly({ titleForAnalytics: "User - Sign In or Up" })
    )

    if (this.validateEmail()) {
      this.rpc
        .execute("USER_CHECK_FOR_ACCOUNT", this.state.tempEmail)
        .then((result) => {
          result.attributes.userExists
            ? this.router.transitionTo("user.sign-in")
            : this.router.transitionTo("user.sign-up")
        })
        .catch((result) => {
          console.error("error", result)
          // This wasn't handled in the original implementation, so putting this here
          this.router.transitionTo("user.sign-in")
        })
    } else {
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({ titleForAnalytics: "User - Sign In Error - Invalid Email" })
      )
    }
  }

  get inviteId() {
    return this.sessionStore.fetchObject<{ id: string }>("invite")?.id
  }

  get promotionCode() {
    return this.sessionStore.fetchObject("promotionCode") || null
  }

  @action signIn(event) {
    event.preventDefault()
    this.state.currentError = null

    // Because TS isn't able to tell if I do isNil on this.state.tempEmail
    let tempEmail = this.state.tempEmail
    let tempPassword = this.state.tempPassword || ""
    if (this.validateEmail() && !isNil(tempEmail)) {
      this.signInProgress = true
      this.rpc
        .session_authenticate_with_email_and_password({
          sessionId: this.session.id,
          email: tempEmail,
          password: tempPassword,
          inviteId: this.inviteId,
        })
        .then((res) => {
          if (res.responseOk === true) {
            this.handleSignInResponse(res)
            this.dispatcher.dispatchPreparedAction(
              analyticsOnly({
                titleForAnalytics: "User - Sign In",
                context: {
                  Method: "Email and Password",
                },
              })
            )
          } else {
            switch (res.result.code) {
              case "NO_PASSWORD_CREATED":
                this.state.currentError = "NO_PASSWORD_CREATED"
                break
              case "NO_PASSWORD_BECAUSE_GOOGLE_AUTH":
                this.state.currentError = "NO_PASSWORD_BECAUSE_GOOGLE_AUTH"
                break
              case "EMAIL_NOT_DELIVERABLE":
                this.state.currentError = "EMAIL_NOT_DELIVERABLE"
                break
              case "LOGIN_RATE_LIMITED":
                this.state.currentError = "LOGIN_RATE_LIMITED"
                break
              default:
                this.state.currentError = "INCORRECT_PASSWORD"
            }
            this.dispatcher.dispatchPreparedAction(
              analyticsOnly({ titleForAnalytics: "User - Sign In Error - Incorrect Password" })
            )
          }
        })
        .finally(() => {
          this.signInProgress = false
        })
        .catch((res) => {
          this.state.currentError = "GENERIC"
          console.log("error", res)
        })
    }
  }

  @action
  signUp(event: Event) {
    event.preventDefault()
    this.state.currentError = null

    // so TS doesn't tell us they're null on rpc.user_create. TS can't tell we're checking
    // a nested path :-(
    let tempEmail = this.state.tempEmail
    let tempPassword = this.state.tempPassword
    if (
      this.validateEmail() &&
      this.validatePassword() &&
      !isNil(tempEmail) &&
      !isNil(tempPassword)
    ) {
      let promotionCode = this.promotionCode

      this.signInProgress = true
      this.rpc
        .user_create({
          sessionId: this.session.id,
          email: tempEmail,
          password: tempPassword,
          promotionCode: promotionCode,
          inviteId: this.sessionStore.fetchObject<{ id: string }>("invite")?.id,
        })
        .then((response) => {
          if (response.responseOk === true) {
            this.handleSignUpResponse(response)
            this.dispatcher.dispatchPreparedAction(
              analyticsOnly({
                titleForAnalytics: "User - Sign Up",
                context: {
                  Method: "Email and Password",
                  value: parseFloat("0"),
                  currency: "USD",
                },
              })
            )
          } else if (response.responseOk === false) {
            if (response.result.status === 401) {
              switch (response.result.code) {
                case "NO_PASSWORD_CREATED":
                  this.state.currentError = "NO_PASSWORD_CREATED"
                  break
                case "NO_PASSWORD_BECAUSE_GOOGLE_AUTH":
                  this.state.currentError = "NO_PASSWORD_BECAUSE_GOOGLE_AUTH"
                  break
                case "EMAIL_NOT_DELIVERABLE":
                  this.state.currentError = "EMAIL_NOT_DELIVERABLE"
                  break
                default:
                  this.state.currentError = "ALREADY_CREATED_ACCOUNT_BUT_PASSWORDS_DONT_MATCH"
              }

              this.dispatcher.dispatchPreparedAction(
                analyticsOnly({ titleForAnalytics: "User - Sign Up Error - User Exists" })
              )
              this.router.transitionTo("user.sign-in")
            } else if (response.result.status == 422) {
              switch (response.result.code) {
                case "INVALID_EMAIL":
                  this.state.currentError = "INVALID_EMAIL"
                  this.dispatcher.dispatchPreparedAction(
                    analyticsOnly({
                      titleForAnalytics: "User - Sign Up Error - Invalid Email",
                    })
                  )
                  break
                default:
                  this.state.currentError = "GENERIC"
              }
            } else {
              this.state.currentError = "GENERIC"
              this.dispatcher.dispatchPreparedAction(
                analyticsOnly({ titleForAnalytics: "User - Sign Up Error - On Server" })
              )
            }
          }
        })
        .finally(() => {
          this.signInProgress = false
        })
        .catch((res) => {
          this.state.currentError = "GENERIC"
          console.log("error", res)
        })
    } else {
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({ titleForAnalytics: "User - Sign Up Error - On Form" })
      )
    }
  }

  handleSignUpResponse(response) {
    let user = response.result.data
    let session = find(response.result.included, { type: "session" })
    // if the user has already started, let's fill in the falues
    this.state.firstName = user.attributes.firstName
    this.state.lastName = user.attributes.lastName

    this.finder.insertResponseDocs(
      response.result,
      getOwner(this)!.lookup("adapter:user"),
      "user",
      [user.id]
    )
    this.session.setTokenAndAuthenticate(session)
    this.session.sendUserToAnalytics()

    // Clear the promotion code and invite from the session store
    this.sessionStore.clear("promotionCode")
    this.sessionStore.clear("invite")

    // Hanndle redirection
    this.redirectOrTransitionAfterSignInOrUp(user)
  }

  handleSignInResponse(res: any) {
    let session = res.result.data
    let user = find(res.result.included, { type: "user" })

    // in case they're still onboarding.
    this.state.firstName = user.attributes.firstName
    this.state.lastName = user.attributes.lastName

    this.finder.insertResponseDocs(res.result, getOwner(this)!.lookup("adapter:user"), "user", [
      user.id,
    ])
    this.session.setTokenAndAuthenticate(session)
    this.session.sendUserToAnalytics()

    // Hanndle redirection
    this.redirectOrTransitionAfterSignInOrUp(user)
  }

  redirectOrTransitionAfterSignInOrUp(user) {
    let urlParams = new URLSearchParams(window.location.search)
    let afterSignInRedirectTo = urlParams.get("afterSignInRedirectTo")

    if (user?.attributes.onboardingInProgress) {
      let step = user?.attributes.lastOnboardingStep
      console.log("redirectOrTransitionAfterSignInOrUp", step)
      ;(getOwner(this)!.lookup("router:main") as Router).transitionTo("user.onboarding", {
        queryParams: { step: step },
      })
      // Note: we just drop afterSignInRedirectTo in this case (hopefully rare).
    } else if (afterSignInRedirectTo) {
      this.socket.beginRedirect()
      // We set this so that we know not to check for unsynced actions
      this.socket.isRedirectingAfterSignIn
      window.location.assign(afterSignInRedirectTo)
    } else {
      ;(getOwner(this)!.lookup("router:main") as Router).transitionTo("user.show", user.id)
    }
  }

  @action
  updateAccount(event: Event) {
    event.preventDefault()
    this.state.currentError = null

    let hasFirstName = (this.state.firstName ?? "").length >= 1
    let hasLastName = (this.state.lastName ?? "").length >= 1
    let hasEducationRole = (this.state.educationRole ?? "").length >= 1

    if (!(hasFirstName && hasLastName)) {
      this.state.currentError = "FULL_NAME"
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({ titleForAnalytics: "User - Sign Up Error - On Account Info" })
      )
    } else if (!hasEducationRole) {
      this.state.currentError = "EDUCATION_ROLE"
      this.dispatcher.dispatchPreparedAction(
        analyticsOnly({ titleForAnalytics: "User - Sign Up Error - On Account Info" })
      )
    } else {
      // update the account
      this.dispatcher.dispatch("USER_ONBOARDING_UPDATE_ACCOUNT", {
        userId: this.session.userId,
        firstName: this.state.firstName,
        lastName: this.state.lastName,
        salutation: this.state.salutation,
        educationRole: this.state.educationRole,
        lastOnboardingStep: 2,
      })

      // After signup, trigger the user observer so we can update with the first/last name.
      this.session.sendUserToAnalytics()

      // redirect
      // @ts-ignore
      ;(getOwner(this)!.lookup("router:main") as Router).transitionTo({ queryParams: { step: 3 } })
    }
  }

  @action
  moveToCreatePlanbook(event: Event) {
    event.preventDefault()
    // @ts-ignore
    ;(getOwner(this)!.lookup("router:main") as Router).transitionTo({ queryParams: { step: 4 } })
  }

  @action
  updateField(fieldName, val) {
    // I **think** this is called a form is blurred as the component is being torn down
    // on transition to user.show. And then this fires but it's already torn down so
    // we don't have session.id
    if (this.isDestroyed || this.isDestroying) return
    if (val === undefined) return
    this.state[fieldName] = val
  }
}
